import React, { useEffect, useState } from 'react';
import { api, fmt, fmtDate } from '../utils/api';
import Topbar from '../components/Topbar';
import { Badge, EmptyState, Spinner, useToast, Modal } from '../components/UI';

export default function Xero({ user, directorView }) {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showToast, Toast] = useToast();
  const [manualModal, setManualModal] = useState(false);
  const [manualForm, setManualForm] = useState({ franchiseId:'', month:'', royalty:'', contactName:'' });
  const [creating, setCreating] = useState(false);
  const isAdmin = user?.role === 'admin';

  const load = () => {
    Promise.all([api.getXeroStatus(), api.getXeroLog(), api.getFranchises(directorView)]).then(([s, l, f]) => {
      setStatus(s);
      setLog(l || []);
      setFranchises(f || []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const failed = log.filter(l => l.status === 'failed');
  const created = log.filter(l => l.status === 'created');

  async function createManualInvoice() {
    if (!manualForm.franchiseId || !manualForm.month || !manualForm.royalty) {
      showToast('Please fill in all required fields'); return;
    }
    setCreating(true);
    const franchise = franchises.find(f => f.id === manualForm.franchiseId);
    try {
      await api.createXeroInvoiceForRoyalty({
        franchiseId: manualForm.franchiseId,
        franchiseName: franchise?.name || manualForm.contactName,
        month: manualForm.month,
        royalty: parseFloat(manualForm.royalty),
      });
      showToast('Invoice created!');
      setManualModal(false);
      load();
    } catch (e) {
      showToast('Failed: ' + e.message);
    }
    setCreating(false);
  }

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Xero" /><Spinner /></div>;

  const tokenExpired = status?.tokenExpired;
  const tokenExpiresAt = status?.expiresAt ? new Date(status.expiresAt) : null;

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Xero Integration" subtitle="Invoice creation and Xero connection management" />
      {Toast}

      <div style={{ padding:24 }}>
        {/* Connection status */}
        <div className="card" style={{ padding:20, marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:48, height:48, borderRadius:'var(--r-md)', background:'var(--blue-bg)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className="ti ti-file-invoice" style={{ fontSize:24, color:'var(--navy)' }} />
              </div>
              <div>
                <div style={{ fontWeight:600, fontSize:15 }}>Xero Connection</div>
                <div style={{ fontSize:12, color:'var(--text-tertiary)', marginTop:2 }}>
                  {tokenExpired ? (
                    <span style={{ color:'var(--red)' }}>⚠ Token expired — reconnect to create invoices</span>
                  ) : tokenExpiresAt ? (
                    <span style={{ color:'var(--green)' }}>✓ Connected · Expires {fmtDate(tokenExpiresAt)}</span>
                  ) : (
                    <span style={{ color:'var(--text-tertiary)' }}>Not connected</span>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <a href={`${process.env.REACT_APP_API_URL || 'https://api.goinvestors.co.uk/api'}/xero/connect`}
                className="btn btn-primary btn-sm">
                {tokenExpired ? 'Reconnect Xero' : 'Connect Xero'}
              </a>
              {isAdmin && !tokenExpired && (
                <button className="btn btn-secondary btn-sm" onClick={() => setManualModal(true)}>
                  <i className="ti ti-plus" style={{ fontSize:13 }} /> Create Invoice
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
          {[
            { label:'Invoices Created', value:created.length, color:'var(--green)', icon:'ti-check' },
            { label:'Failed Attempts', value:failed.length, color:failed.length>0?'var(--red)':'var(--text-tertiary)', icon:'ti-alert-triangle' },
            { label:'Total Invoiced', value:fmt(created.reduce((s,l)=>s+(parseFloat(l.royalty)||0),0)), color:'var(--navy)', icon:'ti-coin' },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding:'12px 16px', display:'flex', gap:10, alignItems:'center' }}>
              <div style={{ width:36, height:36, borderRadius:'var(--r-md)', background:'var(--blue-bg)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className={`ti ${s.icon}`} style={{ fontSize:16, color:s.color }} />
              </div>
              <div>
                <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{s.label}</div>
                <div style={{ fontSize:18, fontWeight:700, color:s.color }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Failed invoices */}
        {failed.length > 0 && (
          <div className="card" style={{ overflow:'hidden', marginBottom:16, border:'1px solid rgba(239,68,68,0.2)' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:8 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize:16, color:'var(--red)' }} />
              <span style={{ fontSize:13, fontWeight:600, color:'var(--red)' }}>Needs Attention ({failed.length})</span>
            </div>
            <table className="table">
              <thead><tr><th>BRANCH</th><th>COMPANY</th><th>MONTH</th><th>ROYALTY</th><th>REASON</th><th>DATE</th>{isAdmin&&<th></th>}</tr></thead>
              <tbody>
                {failed.map(l => (
                  <tr key={l.id}>
                    <td style={{ fontWeight:600, fontSize:13 }}>{l.branch_name||l.branchName||'—'}</td>
                    <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{(() => { const xf=franchises.find(f=>f.name===(l.franchise_name||l.franchiseName)); return xf?(xf.location||xf.name)+(xf.location?' ('+xf.name+')':''):(l.franchise_name||l.franchiseName||'—'); })()}</td>
                    <td style={{ fontSize:12 }}>{l.month}</td>
                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(l.royalty)}</td>
                    <td style={{ fontSize:11, color:'var(--red)', maxWidth:280 }}>{l.reason||l.error||'Unknown error'}</td>
                    <td style={{ fontSize:11, color:'var(--text-tertiary)' }}>{l.created_at ? new Date(l.created_at).toLocaleDateString('en-GB') : '—'}</td>
                    {isAdmin && (
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-sm btn-primary" style={{ fontSize:11 }} onClick={async () => {
                            try {
                              await api.retryXeroInvoice(l.id);
                              showToast('Invoice created!');
                              load();
                            } catch(e) {
                              showToast('Failed: ' + (e.message || 'Unknown error'));
                            }
                          }}>Retry</button>
                          <button className="btn btn-sm btn-danger" style={{ fontSize:11 }} onClick={async () => {
                            if (!window.confirm('Delete this log entry?')) return;
                            await api.deleteXeroLog(l.id);
                            showToast('Deleted');
                            load();
                          }}>Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Created invoices */}
        <div className="card" style={{ overflow:'hidden' }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:8 }}>
            <i className="ti ti-check" style={{ fontSize:16, color:'var(--green)' }} />
            <span style={{ fontSize:13, fontWeight:600 }}>Created Invoices ({created.length})</span>
          </div>
          {created.length === 0 ? <EmptyState icon="ti-file-invoice" message="No invoices created yet" /> : (
            <table className="table">
              <thead><tr><th>BRANCH</th><th>COMPANY</th><th>MONTH</th><th>ROYALTY</th><th>INVOICE #</th><th>CONTACT</th><th>ACCOUNT CODE</th><th>DATE</th></tr></thead>
              <tbody>
                {created.slice().reverse().map(l => (
                  <tr key={l.id}>
                    <td style={{ fontWeight:600, fontSize:13 }}>{l.branch_name||l.branchName||'—'}</td>
                    <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{(() => { const xf=franchises.find(f=>f.name===(l.franchise_name||l.franchiseName)); return xf?(xf.location||xf.name)+(xf.location?' ('+xf.name+')':''):(l.franchise_name||l.franchiseName||'—'); })()}</td>
                    <td style={{ fontSize:12 }}>{l.month}</td>
                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--green)', fontWeight:600 }}>{fmt(l.royalty)}</td>
                    <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--blue)' }}>{l.invoice_number||l.invoiceNumber||'—'}</td>
                    <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{l.contact_name||l.contactName||'—'}</td>
                    <td style={{ fontSize:11, color:'var(--text-secondary)' }}>{l.account_code||l.accountCode||'—'}</td>
                    <td style={{ fontSize:11, color:'var(--text-tertiary)' }}>{l.created_at||l.createdAt ? new Date(l.created_at||l.createdAt).toLocaleDateString('en-GB') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Manual Invoice Modal */}
      {manualModal && (
        <Modal title="Create Xero Invoice" onClose={() => setManualModal(false)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setManualModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={createManualInvoice} disabled={creating}>
              {creating ? 'Creating...' : 'Create Invoice'}
            </button>
          </>}>
          <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:12 }}>
            Manually create a Xero invoice for a royalty payment.
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div><label>BRANCH *</label>
              <select value={manualForm.franchiseId} onChange={e=>setManualForm(f=>({...f,franchiseId:e.target.value}))}>
                <option value="">Select branch...</option>
                {franchises.filter(f=>f.status==='active').map(f=><option key={f.id} value={f.id}>{f.location||f.name}{f.location?' ('+f.name+')':''}</option>)}
              </select>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div><label>MONTH *</label><input value={manualForm.month} placeholder="e.g. May 2026" onChange={e=>setManualForm(f=>({...f,month:e.target.value}))} /></div>
              <div><label>ROYALTY AMOUNT (£) *</label><input type="number" value={manualForm.royalty} onChange={e=>setManualForm(f=>({...f,royalty:e.target.value}))} /></div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
